import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Link as LinkIcon,
  Copy,
  FileText,
  Trash2,
  Loader2,
  Send,
  Search,
  CheckCircle2,
  Clock,
  CircleDashed,
  GraduationCap,
  BookOpen,
  Calendar,
  X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  not_started: { label: 'Not Started', icon: CircleDashed, className: 'bg-muted text-muted-foreground' },
  in_progress: { label: 'In Progress', icon: Clock, className: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Completed', icon: CheckCircle2, className: 'bg-emerald-100 text-emerald-800' },
};

const DATE_FILTERS = [
  { value: 'all', label: 'Any time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
];

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const isWithinDateFilter = (dateString, filter) => {
  if (filter === 'all') return true;
  const created = new Date(dateString).getTime();
  const now = Date.now();
  const days = filter === 'today' ? 1 : filter === 'week' ? 7 : 30;
  return now - created <= days * 24 * 60 * 60 * 1000;
};

const formatStudentName = (s) => {
  const last = s.last_name ? ` ${s.last_name}` : '';
  return `${s.first_name}${last}`;
};

const AssignmentsPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create-modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [generateLink, setGenerateLink] = useState(true);
  const [creating, setCreating] = useState(false);
  const [modalFilterSubject, setModalFilterSubject] = useState('all');
  const [modalFilterGrade, setModalFilterGrade] = useState('all');

  // Page filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStudent, setFilterStudent] = useState('all');
  const [filterGrade, setFilterGrade] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [assignmentsRes, quizzesRes, studentsRes] = await Promise.all([
        axios.get(`${API}/assignments/detailed`, { headers }),
        axios.get(`${API}/quizzes`, { headers }),
        axios.get(`${API}/students`, { headers }),
      ]);
      setAssignments(assignmentsRes.data);
      setQuizzes(quizzesRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedQuiz) {
      toast.error('Please select a quiz');
      return;
    }
    if (!generateLink && selectedStudents.length === 0) {
      toast.error('Select at least one student or enable shareable link');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/assignments`,
        {
          quiz_id: selectedQuiz,
          student_ids: selectedStudents.length > 0 ? selectedStudents : null,
          generate_link: generateLink,
        },
        { headers: getAuthHeader() }
      );
      toast.success('Assignment created');
      if (generateLink && response.data.shareable_code) {
        const link = `${window.location.origin}/student/quiz/${response.data.shareable_code}`;
        try {
          await navigator.clipboard.writeText(link);
          toast.info('Shareable link copied to clipboard');
        } catch {
          // ignore clipboard errors
        }
      }
      closeModal();
      fetchData();
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    } finally {
      setCreating(false);
    }
  };

  const handleRemoveStudent = async (assignmentId, studentId) => {
    if (!window.confirm('Remove this student from this assignment? The quiz itself will not be deleted.')) return;
    try {
      await axios.delete(`${API}/assignments/${assignmentId}/students/${studentId}`, {
        headers: getAuthHeader(),
      });
      toast.success('Student removed from assignment');
      fetchData();
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Failed to remove student');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Delete this assignment entirely? This will not delete the quiz from your library.')) return;
    try {
      await axios.delete(`${API}/assignments/${assignmentId}`, { headers: getAuthHeader() });
      toast.success('Assignment deleted');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete assignment');
    }
  };

  const copyLink = async (code) => {
    const link = `${window.location.origin}/student/quiz/${code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied!');
    } catch {
      toast.info(`Link: ${link}`);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedQuiz('');
    setSelectedStudents([]);
    setGenerateLink(true);
    setModalFilterSubject('all');
    setModalFilterGrade('all');
  };

  const toggleStudent = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterStudent('all');
    setFilterGrade('all');
    setFilterSubject('all');
    setFilterStatus('all');
    setFilterDate('all');
  };

  // Derived option sets
  const subjectOptions = useMemo(
    () => [...new Set(assignments.map((a) => a.subject).filter(Boolean))].sort(),
    [assignments]
  );
  const gradeOptions = useMemo(() => {
    const fromAssignments = assignments.flatMap((a) => [a.grade_level, ...a.students.map((s) => s.grade_level)]);
    return [...new Set(fromAssignments.filter(Boolean))].sort();
  }, [assignments]);

  // Modal filter source
  const uniqueQuizSubjects = useMemo(
    () => [...new Set(quizzes.map((q) => q.subject).filter(Boolean))].sort(),
    [quizzes]
  );
  const uniqueQuizGrades = useMemo(
    () => [...new Set(quizzes.map((q) => q.grade_level).filter(Boolean))].sort(),
    [quizzes]
  );
  const filteredQuizzes = quizzes.filter((quiz) => {
    const matchesSubject = modalFilterSubject === 'all' || quiz.subject === modalFilterSubject;
    const matchesGrade = modalFilterGrade === 'all' || quiz.grade_level === modalFilterGrade;
    return matchesSubject && matchesGrade;
  });

  // Apply page filters / search
  const visibleAssignments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return assignments
      .map((a) => {
        const matchingStudents = a.students.filter((s) => {
          if (filterStudent !== 'all' && s.id !== filterStudent) return false;
          if (filterGrade !== 'all' && (s.grade_level || a.grade_level) !== filterGrade) return false;
          if (filterStatus !== 'all' && s.status !== filterStatus) return false;
          if (q) {
            const titleMatch = (a.quiz_title || '').toLowerCase().includes(q);
            const nameMatch = formatStudentName(s).toLowerCase().includes(q);
            if (!titleMatch && !nameMatch) return false;
          }
          return true;
        });
        return { ...a, students: matchingStudents };
      })
      .filter((a) => {
        if (filterSubject !== 'all' && a.subject !== filterSubject) return false;
        if (!isWithinDateFilter(a.created_at, filterDate)) return false;
        // If a status/student/grade filter is on or search is on, hide assignments with no matching students
        const hasActiveStudentFilter =
          filterStudent !== 'all' || filterGrade !== 'all' || filterStatus !== 'all' || !!q;
        if (hasActiveStudentFilter) return a.students.length > 0;
        return true;
      });
  }, [assignments, searchQuery, filterStudent, filterGrade, filterSubject, filterStatus, filterDate]);

  const hasAnyFilter =
    !!searchQuery ||
    filterStudent !== 'all' ||
    filterGrade !== 'all' ||
    filterSubject !== 'all' ||
    filterStatus !== 'all' ||
    filterDate !== 'all';

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6"
        >
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-1">Quiz Assignments</h1>
            <p className="text-muted-foreground">Track who has what — and keep things organized.</p>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            className="gap-2 bg-primary hover:bg-primary/90"
            disabled={quizzes.length === 0}
            data-testid="assign-quiz-btn"
          >
            <Send className="w-4 h-4" />
            Assign Quiz
          </Button>
        </motion.div>

        {/* Search + Filters */}
        <Card className="border-border mb-6">
          <CardContent className="p-4 space-y-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by quiz title or student name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="assignments-search"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Student</Label>
                <Select value={filterStudent} onValueChange={setFilterStudent}>
                  <SelectTrigger data-testid="filter-student"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All students</SelectItem>
                    {students.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{formatStudentName(s)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Grade Level</Label>
                <Select value={filterGrade} onValueChange={setFilterGrade}>
                  <SelectTrigger data-testid="filter-grade"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All grades</SelectItem>
                    {gradeOptions.map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Subject</Label>
                <Select value={filterSubject} onValueChange={setFilterSubject}>
                  <SelectTrigger data-testid="filter-subject"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All subjects</SelectItem>
                    {subjectOptions.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Date Assigned</Label>
                <Select value={filterDate} onValueChange={setFilterDate}>
                  <SelectTrigger data-testid="filter-date"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DATE_FILTERS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {hasAnyFilter && (
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  Showing {visibleAssignments.length} of {assignments.length} assignments
                </p>
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="gap-1" data-testid="clear-filters">
                  <X className="w-3 h-3" /> Clear filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assignment Cards */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-border">
                <CardContent className="p-6">
                  <Skeleton className="h-6 w-48 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : visibleAssignments.length > 0 ? (
          <div className="space-y-4">
            <AnimatePresence>
              {visibleAssignments.map((assignment, index) => (
                <motion.div
                  key={assignment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.04 }}
                >
                  <Card className="border-border hover:border-primary/30 transition-colors" data-testid={`assignment-card-${index}`}>
                    <CardContent className="p-6">
                      {/* Quiz header row */}
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-6 h-6 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-serif text-xl font-medium text-primary leading-tight" data-testid={`assignment-title-${index}`}>
                              {assignment.quiz_title}
                            </h3>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                              {assignment.subject && (
                                <span className="inline-flex items-center gap-1">
                                  <BookOpen className="w-3.5 h-3.5" />
                                  Subject: <span className="text-foreground font-medium">{assignment.subject}</span>
                                </span>
                              )}
                              {assignment.grade_level && (
                                <span className="inline-flex items-center gap-1">
                                  <GraduationCap className="w-3.5 h-3.5" />
                                  Grade: <span className="text-foreground font-medium">{assignment.grade_level}</span>
                                </span>
                              )}
                              <span className="inline-flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                Assigned: <span className="text-foreground font-medium">{formatDate(assignment.created_at)}</span>
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {assignment.shareable_code && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyLink(assignment.shareable_code)}
                              className="gap-2"
                              data-testid={`copy-link-${index}`}
                            >
                              <Copy className="w-4 h-4" />
                              Copy Link
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteAssignment(assignment.id)}
                            className="hover:text-destructive"
                            title="Delete entire assignment"
                            data-testid={`delete-assignment-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Per-student rows */}
                      {assignment.students.length > 0 ? (
                        <div className="border-t pt-3">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                            Assigned to ({assignment.students.length})
                          </p>
                          <div className="divide-y divide-border">
                            {assignment.students.map((s, sIdx) => {
                              const meta = STATUS_META[s.status] || STATUS_META.not_started;
                              const StatusIcon = meta.icon;
                              return (
                                <div
                                  key={s.id}
                                  className="flex items-center justify-between py-2 gap-3"
                                  data-testid={`assignment-${index}-student-${sIdx}`}
                                >
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                                      <span className="text-sm font-medium text-primary">
                                        {(s.first_name || '?').charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-medium text-foreground truncate">
                                        {formatStudentName(s)}
                                        {s.grade_level && (
                                          <span className="text-muted-foreground font-normal"> — {s.grade_level}</span>
                                        )}
                                      </p>
                                      {s.status === 'completed' && typeof s.percentage === 'number' && (
                                        <p className="text-xs text-muted-foreground">Score: {Math.round(s.percentage)}%</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <Badge className={`${meta.className} gap-1 font-normal`} variant="outline">
                                      <StatusIcon className="w-3 h-3" />
                                      {meta.label}
                                    </Badge>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 hover:text-destructive"
                                      onClick={() => handleRemoveStudent(assignment.id, s.id)}
                                      title="Remove this student from assignment"
                                      data-testid={`remove-student-${index}-${sIdx}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="border-t pt-3">
                          <p className="text-xs text-muted-foreground inline-flex items-center gap-2">
                            <LinkIcon className="w-3 h-3" />
                            Shareable link only — no individual students assigned
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="font-serif text-xl font-medium text-primary mb-2">
              {hasAnyFilter ? 'No assignments match your filters' : 'No assignments yet'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {hasAnyFilter
                ? 'Try clearing some filters or your search query.'
                : quizzes.length === 0
                ? 'Create a quiz first, then assign it to students.'
                : 'Assign a quiz to your students to get started.'}
            </p>
            {hasAnyFilter ? (
              <Button onClick={clearAllFilters} variant="outline" className="gap-2">
                <X className="w-4 h-4" />
                Clear filters
              </Button>
            ) : quizzes.length > 0 ? (
              <Button onClick={() => setShowModal(true)} className="gap-2">
                <Send className="w-4 h-4" />
                Assign Your First Quiz
              </Button>
            ) : (
              <Button onClick={() => navigate('/create')} className="gap-2">
                <Plus className="w-4 h-4" />
                Create a Quiz
              </Button>
            )}
          </motion.div>
        )}
      </main>

      {/* Create Assignment Modal */}
      <Dialog open={showModal} onOpenChange={closeModal}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-serif">Assign Quiz</DialogTitle>
            <DialogDescription>Select a quiz and how you want to share it with students.</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4 overflow-y-auto flex-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Filter by Subject</Label>
                <Select value={modalFilterSubject} onValueChange={setModalFilterSubject}>
                  <SelectTrigger data-testid="modal-filter-subject"><SelectValue placeholder="All Subjects" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subjects</SelectItem>
                    {uniqueQuizSubjects.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Filter by Grade</Label>
                <Select value={modalFilterGrade} onValueChange={setModalFilterGrade}>
                  <SelectTrigger data-testid="modal-filter-grade"><SelectValue placeholder="All Grades" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Grades</SelectItem>
                    {uniqueQuizGrades.map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Select Quiz{' '}
                {filteredQuizzes.length !== quizzes.length && (
                  <span className="text-muted-foreground text-xs">
                    ({filteredQuizzes.length} of {quizzes.length})
                  </span>
                )}
              </Label>
              <Select value={selectedQuiz} onValueChange={setSelectedQuiz}>
                <SelectTrigger data-testid="quiz-select"><SelectValue placeholder="Choose a quiz" /></SelectTrigger>
                <SelectContent>
                  {filteredQuizzes.length > 0 ? (
                    filteredQuizzes.map((quiz) => (
                      <SelectItem key={quiz.id} value={quiz.id}>
                        <div className="flex flex-col">
                          <span>{quiz.title}</span>
                          <span className="text-xs text-muted-foreground">
                            {quiz.subject} • {quiz.grade_level}
                          </span>
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                      No quizzes match the selected filters
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 p-4 bg-secondary/50 rounded-lg">
              <Checkbox
                id="generateLink"
                checked={generateLink}
                onCheckedChange={setGenerateLink}
                data-testid="generate-link-checkbox"
              />
              <div>
                <Label htmlFor="generateLink" className="cursor-pointer font-medium">
                  Generate shareable link
                </Label>
                <p className="text-xs text-muted-foreground">Anyone with the link can take the quiz</p>
              </div>
            </div>

            {students.length > 0 && (
              <div className="space-y-3">
                <Label>Assign to specific students (optional)</Label>
                <div className="max-h-48 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                  {students.map((student) => (
                    <div
                      key={student.id}
                      className="flex items-center gap-3 p-3 hover:bg-secondary/50 cursor-pointer"
                      onClick={() => toggleStudent(student.id)}
                    >
                      <Checkbox
                        checked={selectedStudents.includes(student.id)}
                        onCheckedChange={() => toggleStudent(student.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">{formatStudentName(student)}</p>
                        {student.grade_level && (
                          <p className="text-xs text-muted-foreground">{student.grade_level}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {selectedStudents.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {selectedStudents.length} student{selectedStudents.length > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !selectedQuiz} data-testid="create-assignment-btn">
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Assignment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssignmentsPage;
